import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { displayName } from "@/utils/sessionBadges";
import { DEMO_PATH, INQUIRE_URL } from "@/constants/publicSite";
import "@fontsource-variable/figtree";
import "./Features.css";

type Chapter = {
  id: string;
  step: string;
  title: string;
  pose: string;
  poseAlt: string;
  say: string;
  tinted?: boolean;
  screen: ReactNode;
};

function Frame({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="alf-screen">
      <div className="alf-screen-bar" aria-hidden="true">
        <span />
        <span />
        <span />
        <em>{label}</em>
      </div>
      <div className="alf-screen-body">{children}</div>
    </div>
  );
}

function Chip({ tone = "plain", children }: { tone?: "plain" | "brand" | "good" | "warn" | "solid"; children: ReactNode }) {
  return <span className={`alf-chip alf-chip--${tone}`}>{children}</span>;
}

/** Try it: a flashcard the visitor can flip. */
function FlashcardTry() {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="alf-cards">
      <button
        type="button"
        className={`alf-flash alf-flash--try${flipped ? " is-flipped" : ""}`}
        aria-pressed={flipped}
        onClick={() => setFlipped((on) => !on)}
      >
        <small>{flipped ? "Definition" : "Try it · flashcard 5 of 6"}</small>
        {flipped ? (
          <span className="alf-flash-def">
            The number multiplied by a variable. In <code>7x</code>, the coefficient is 7.
          </span>
        ) : (
          <strong>Coefficient</strong>
        )}
        <small>{flipped ? "Click to flip back" : "Click to flip"}</small>
      </button>
    </div>
  );
}

const TRY_OPTIONS = ["4x - 9", "3x + 1 = 7", "2(a + 5)", "n ÷ 6"];
const TRY_RIGHT = 1;

/** Try it: answer one check question the way a student would. */
function QuestionTry() {
  const [pick, setPick] = useState<number | null>(null);
  const right = pick === TRY_RIGHT;
  return (
    <div className="alf-try">
      <p className="alf-label">Try it as a student</p>
      <p className="alf-try-q">Which of these is an equation, not an expression?</p>
      <div className="alf-try-options" role="group" aria-label="Answer choices">
        {TRY_OPTIONS.map((label, index) => {
          const state = pick === index ? (index === TRY_RIGHT ? " is-right" : " is-wrong") : "";
          return (
            <button
              key={label}
              type="button"
              className={`alf-try-option${state}`}
              aria-pressed={pick === index}
              onClick={() => setPick(index)}
            >
              <code>{label}</code>
            </button>
          );
        })}
      </div>
      <p className={`alf-try-feedback${right ? " is-right" : ""}`} aria-live="polite">
        {pick === null ? "" : right ? "Correct. It has an equals sign, so it makes a claim." : "Not quite. Look for the equals sign."}
      </p>
    </div>
  );
}

const OUTLINE = (
  <Frame label="Course builder">
    <p className="alf-muted alf-small">Courses</p>
    <h3 className="alf-h3">Algebra 1</h3>
    <div className="alf-chips">
      <Chip>Walkthrough</Chip>
      <Chip>Preview course</Chip>
      <Chip tone="good">Unit 1 complete</Chip>
    </div>
    <div className="alf-split">
      <strong>Outline</strong>
      <Chip>Use standard template</Chip>
    </div>
    <div className="alf-list">
      <div className="alf-row alf-row--head"><span>Unit 1 · Foundations of Algebra</span><small>4 lessons · exam</small></div>
      <div className="alf-row alf-row--in"><span>1.1 Variables and Expressions</span><small>7 items · 100 min</small></div>
      <div className="alf-row alf-row--in"><span>1.2 Order of Operations</span><small>7 items · 100 min</small></div>
      <div className="alf-row alf-row--in"><span>1.3 Properties of Real Numbers</span><small>7 items · 100 min</small></div>
      <div className="alf-row alf-row--in"><span>1.4 Solving One-Step Equations</span><small>7 items · 100 min</small></div>
      <div className="alf-row alf-row--in"><span>Unit 1 Exam</span><small>20 questions · 45 min</small></div>
      <div className="alf-row alf-row--head"><span>Unit 2 · Solving Equations</span><small>4 lessons · exam</small></div>
    </div>
  </Frame>
);

const CONTENT_TYPES = [
  ["Reading", "Articles, PDFs, sources"],
  ["Video", "Intro or tutorial"],
  ["Audio", "Recordings and talks"],
  ["Slides", "A Canva or PDF deck"],
  ["Key terms", "Words with definitions"],
];
const GRADED_TYPES = [
  ["Assessment", "Quick checks"],
  ["Exam", "Attempts and time limit"],
  ["Homework", "Practice or uploads"],
  ["Discussion", "Prompt and replies"],
  ["Project", "Extended work"],
  ["Lab", "Hands-on tasks"],
];

const ITEM_TYPES = (
  <Frame label="Select item type">
    <p className="alf-label">Content</p>
    <div className="alf-tiles">
      {CONTENT_TYPES.map(([name, note]) => (
        <div key={name} className={`alf-tile${name === "Key terms" ? " alf-tile--on" : ""}`}>
          <strong>{name}</strong>
          <small>{note}</small>
        </div>
      ))}
    </div>
    <p className="alf-label">Graded</p>
    <div className="alf-tiles">
      {GRADED_TYPES.map(([name, note]) => (
        <div key={name} className="alf-tile">
          <strong>{name}</strong>
          <small>{note}</small>
        </div>
      ))}
    </div>
  </Frame>
);

const READING = (
  <div className="alf-pair">
    <Frame label="Reading · Lesson 1.1">
      <Chip tone="brand">Reading</Chip>
      <h3 className="alf-h3">What a Variable Really Is</h3>
      <p className="alf-listen">Listen · 20 min</p>
      <p className="alf-strong">A letter is a placeholder, not a mystery</p>
      <p className="alf-body">
        A variable is a symbol, usually a letter, that stands for a number we do not know yet. When you write{" "}
        <code>x + 3</code>, you are writing an instruction: take some number and add three to it.
      </p>
      <div className="alf-table">
        <div><b>Term</b><b>Kind</b><b>Number in front</b></div>
        <div><code>5x</code><span>variable term</span><span>5</span></div>
        <div><code>8</code><span>constant</span><span>none</span></div>
      </div>
    </Frame>
    <FlashcardTry />
  </div>
);

const QUESTIONS = [
  ["Which of these is an equation, not an expression?", "Single choice"],
  ['Write "six less than a number n" as an expression.', "Single choice"],
  ["How many terms are in 5x − 3y + 8?", "Single choice"],
  ["Select every phrase that means 3x.", "Choose all"],
];

const QUESTION_BUILDER = (
  <div className="alf-stack">
  <Frame label="Check Your Understanding 1.1">
    <div className="alf-chips">
      <Chip>8 questions</Chip>
      <Chip>40 points</Chip>
      <Chip>3 attempts</Chip>
      <Chip>15 min</Chip>
    </div>
    <div className="alf-scroll">
      <table className="alf-qtable">
        <thead>
          <tr>
            <th scope="col">Question</th>
            <th scope="col">Type</th>
            <th scope="col">Points</th>
            <th scope="col">Grading</th>
          </tr>
        </thead>
        <tbody>
          {QUESTIONS.map(([prompt, type]) => (
            <tr key={prompt}>
              <td>{prompt}</td>
              <td className="alf-muted">{type}</td>
              <td>5</td>
              <td><Chip tone="good">Auto</Chip></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Frame>
  <QuestionTry />
  </div>
);

const PATH = [
  ["Reading", "done"],
  ["Audio", "done"],
  ["Key terms", "done"],
  ["Discussion", "done"],
  ["Practice 1.1", "next"],
  ["Homework 1.1", "later"],
  ["Check 1.1", "later"],
];

const STUDENT = (
  <div className="alf-student">
    <div className="alf-phone">
      <p className="alf-muted alf-small">Algebra 1 · Lesson 1.1</p>
      <p className="alf-phone-title">Variables and Expressions</p>
      <div className="alf-progress" aria-hidden="true"><span style={{ width: "57%" }} /></div>
      <p className="alf-small alf-muted">4 of 7 done</p>
      <ul>
        {PATH.map(([name, state]) => (
          <li key={name} className={`alf-path alf-path--${state}`}>
            <Chip tone={state === "done" ? "good" : state === "next" ? "solid" : "plain"}>
              {state === "done" ? "Done" : state === "next" ? "Next" : "Later"}
            </Chip>
            {name}
          </li>
        ))}
      </ul>
    </div>
    <div className="alf-goals">
      <p className="alf-label">By the end of this lesson</p>
      <ul>
        <li>Explain what a variable is.</li>
        <li>Tell an expression apart from an equation.</li>
        <li>Name terms, coefficients and constants.</li>
        <li>Turn a phrase into an expression.</li>
      </ul>
    </div>
  </div>
);

const GRADING = (
  <Frame label="Needs grading">
    <div className="alf-stats">
      <div><small>Checks and exams</small><strong>Graded automatically</strong></div>
      <div><small>Discussions</small><strong>Graded by you</strong></div>
      <div><small>Homework uploads</small><strong>Graded by you</strong></div>
    </div>
    <div className="alf-list">
      <div className="alf-row alf-row--head"><span>Item</span><small>Status</small></div>
      <div className="alf-row"><span>Discussion: Where do you already use variables?</span><Chip tone="warn">Needs a grade</Chip></div>
      <div className="alf-row"><span>Homework 1.2 upload</span><Chip tone="warn">Needs a grade</Chip></div>
      <div className="alf-row"><span>Check Your Understanding 1.3</span><Chip tone="good">Auto</Chip></div>
    </div>
  </Frame>
);

const OPERATIONS = (
  <div className="alf-ops">
    <div className="alf-op">
      <strong>Attendance</strong>
      <small>Algebra 1 · Period 2</small>
      <div className="alf-op-rows">
        <span>Student A <Chip tone="good">Present</Chip></span>
        <span>Student B <Chip tone="warn">Tardy</Chip></span>
        <span>Student C <Chip tone="good">Present</Chip></span>
      </div>
    </div>
    <div className="alf-op">
      <strong>Events</strong>
      <small>Parent–Teacher Conferences</small>
      <p>RSVP, then attendance on the day. Invited families see it on their calendar.</p>
      <Chip tone="brand">RSVP open</Chip>
    </div>
    <div className="alf-op">
      <strong>Email notices</strong>
      <small>Sent automatically</small>
      <p>Enrollments, announcements and replies go out by email, so nobody has to remember to check.</p>
    </div>
  </div>
);

const CHAPTERS: Chapter[] = [
  {
    id: "build",
    step: "1 of 7 · Build",
    title: "Start with an outline",
    pose: "pointing",
    poseAlt: "Butler pointing",
    say: "Every course is units, lessons and items, in any shape you like. Or start from the standard template and fill it in.",
    screen: OUTLINE,
  },
  {
    id: "items",
    step: "2 of 7 · Build",
    title: "Add the right kind of item",
    pose: "thinking",
    poseAlt: "Butler thinking",
    say: "Teach with readings, video, audio and slides. Grade with checks, homework, projects and exams. I'll flag anything that isn't finished yet.",
    tinted: true,
    screen: ITEM_TYPES,
  },
  {
    id: "teach",
    step: "3 of 7 · Teach",
    title: "Lessons students actually read",
    pose: "holding-book",
    poseAlt: "Butler holding a book",
    say: "Readings come with tables, worked examples and a Listen button. Key terms turn into flashcards on their own.",
    screen: READING,
  },
  {
    id: "questions",
    step: "4 of 7 · Teach",
    title: "Questions that grade themselves",
    pose: "holding-laptop",
    poseAlt: "Butler holding a laptop",
    say: "Write a question once. You choose the points and how many tries students get, and the scoring happens on its own.",
    tinted: true,
    screen: QUESTION_BUILDER,
  },
  {
    id: "learn",
    step: "5 of 7 · Learn",
    title: "One clear path for students",
    pose: "reading",
    poseAlt: "Butler reading",
    say: "Students always know what's next, on a laptop or a phone. They see the lesson goals before they start.",
    screen: STUDENT,
  },
  {
    id: "grade",
    step: "6 of 7 · Grade",
    title: "Grades that add themselves up",
    pose: "trophy",
    poseAlt: "Butler holding a trophy",
    say: "Scores land in weighted categories. You only step in for the work that needs a person, like discussions and uploads.",
    tinted: true,
    screen: GRADING,
  },
  {
    id: "run",
    step: "7 of 7 · Run your school",
    title: "More than courses",
    pose: "graduation",
    poseAlt: "Butler wearing a graduation cap",
    say: "Take attendance, run events with RSVPs, and email families when something changes. Same login, same place.",
    screen: OPERATIONS,
  },
];

function DemoButton({ className, canDemo, pending }: { className: string; canDemo: boolean; pending: boolean }) {
  // Until sign-in is known, hold the space so the button doesn't flip from "Request" to "Start".
  if (pending) {
    return (
      <span className={className} style={{ visibility: "hidden" }} aria-hidden="true">
        Request a demo
      </span>
    );
  }
  if (canDemo) {
    return (
      <Link to={DEMO_PATH} className={className}>
        Start demo
        <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
      </Link>
    );
  }
  return (
    <a href={INQUIRE_URL} className={className} target="_blank" rel="noopener noreferrer">
      Request a demo
      <ArrowUpRight size={16} strokeWidth={2} aria-hidden="true" />
    </a>
  );
}

/** How many of the seven stops have scrolled into view. Only ever goes up. */
function useStopsSeen(root: RefObject<HTMLDivElement>, embedded: boolean) {
  const [seen, setSeen] = useState(0);
  useEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    const scroller: HTMLElement | null = embedded ? el.closest(".al-shell-body") : el;
    const target: HTMLElement | Window = scroller || window;
    const measure = () => {
      const view = scroller ? scroller.getBoundingClientRect() : { top: 0, height: window.innerHeight };
      const line = view.top + view.height * 0.75;
      const stops = el.querySelectorAll<HTMLElement>("[data-stop]");
      let n = 0;
      stops.forEach((stop, index) => {
        if (stop.getBoundingClientRect().top < line) n = index + 1;
      });
      setSeen((prev) => (n > prev ? n : prev));
    };
    measure();
    target.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      target.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [root, embedded]);
  return seen;
}

/**
 * The features tour. "public" is the full page at /features and for signed-out
 * visitors. "home" is the signed-in Home page inside the app shell: no header
 * bar, no Inquire here or Request a demo.
 */
export default function Features({ variant = "public" }: { variant?: "public" | "home" }) {
  const { loading, isAuthenticated, hasAccess, pageAccess, user } = useAuth();
  const { hash } = useLocation();
  const root = useRef<HTMLDivElement>(null);
  const home = variant === "home";
  const signedIn = isAuthenticated && hasAccess;
  /** The demo reads a real course, so it needs a signed-in user who can open the course builder. */
  const canDemo = Boolean(signedIn && pageAccess?.["course-builder"]);
  const seen = useStopsSeen(root, home);
  const total = CHAPTERS.length;
  const done = seen >= total;
  const firstName = (user?.preferredName || user?.firstName || "").trim() || displayName(user);

  useEffect(() => {
    document.title = home ? "AcuityLearn" : "AcuityLearn — Features";
  }, [home]);

  useEffect(() => {
    const id = hash.replace(/^#/, "");
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className={`alf${home ? " alf--home" : ""}`} ref={root} id="alf-top">
      {home ? null : (
        <header className="alf-header">
          <div className="alf-wrap alf-header-in">
            <Link to="/" className="alf-brand">
              <img src="/brand/acuity-mark.png" alt="" />
              <span>AcuityLearn</span>
            </Link>
            <nav aria-label="Features" className="alf-nav">
              <a href="#build">Build</a>
              <a href="#teach">Teach</a>
              <a href="#run">Run your school</a>
              {loading ? null : signedIn ? (
                <Link to="/" className="alf-btn alf-btn--small">Open AcuityLearn</Link>
              ) : (
                <Link to="/auth" className="alf-btn alf-btn--small alf-btn--ghost">Sign in</Link>
              )}
            </nav>
          </div>
        </header>
      )}

      <div className="alf-tour" role="status" aria-label={`Tour progress: ${seen} of ${total} explored`}>
        <div className="alf-wrap alf-tour-in">
          <span className="alf-tour-label">Your tour</span>
          <div className="alf-tour-track" aria-hidden="true">
            <span style={{ width: `${Math.round((seen / total) * 100)}%` }} />
          </div>
          <span className="alf-tour-count">
            {seen} of {total} explored
          </span>
        </div>
      </div>

      <main>
        <section className={`alf-hero${home ? " alf-hero--home" : ""}`}>
          <div className="alf-wrap alf-hero-in">
            <div className="alf-hero-copy">
              {home ? (
                <>
                  <p className="alf-eyebrow alf-eyebrow--plain">Welcome back, {firstName}</p>
                  <h1>See everything AcuityLearn can do</h1>
                  <p className="alf-lead">Scroll through seven stops with Butler. You can try a few things right here along the way.</p>
                  <div className="alf-actions">
                    {canDemo ? <DemoButton className="alf-btn" canDemo pending={false} /> : null}
                    <a href="#build" className="alf-btn alf-btn--ghost">
                      Scroll to begin
                      <ArrowDown size={16} strokeWidth={2} aria-hidden="true" />
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <p className="alf-eyebrow">For teachers, curriculum teams and schools</p>
                  <h1>Build a complete course. Then watch students learn from it.</h1>
                  <p className="alf-lead">
                    AcuityLearn puts course building, teaching, grading and school operations in one place. Scroll down and
                    Butler will show you around.
                  </p>
                  <div className="alf-actions">
                    <DemoButton className="alf-btn" canDemo={canDemo} pending={loading} />
                    <a href="#build" className="alf-btn alf-btn--ghost">
                      See how it works
                      <ArrowDown size={16} strokeWidth={2} aria-hidden="true" />
                    </a>
                  </div>
                </>
              )}
            </div>
            <div className="alf-hero-butler">
              <div className="alf-bubble alf-bubble--card">
                <p className="alf-bubble-name">Butler</p>
                <p>
                  {home
                    ? "Hi again! Scroll through all seven stops and I'll show you the demo."
                    : "Hi, I'm Butler. I help teachers build courses here. Scroll with me and I'll show you what you can make."}
                </p>
              </div>
              <img className="alf-bob" src="/mascot/waving.png" alt="Butler, the AcuityLearn guide, waving hello" />
            </div>
          </div>
        </section>

        {home ? null : (
          <section className="alf-wrap alf-audience" aria-label="Who it is for">
            <div>
              <h2>Teachers</h2>
              <p>Teach from a finished course, grade in one place, and see who is stuck.</p>
            </div>
            <div>
              <h2>Curriculum developers</h2>
              <p>Build once from a standard template and share it across classes and schools.</p>
            </div>
            <div>
              <h2>School leaders</h2>
              <p>Courses, attendance, events and family email, without five separate tools.</p>
            </div>
          </section>
        )}

        {CHAPTERS.map((chapter, index) => (
          <section
            key={chapter.id}
            id={chapter.id}
            className={`alf-chapter${chapter.tinted ? " alf-chapter--tinted" : ""}`}
            aria-labelledby={`${chapter.id}-title`}
          >
            <div className={`alf-wrap alf-chapter-in alf-reveal${seen > index ? " is-seen" : ""}`} data-stop={index + 1}>
              <div className="alf-narr">
                <p className="alf-step">{chapter.step}</p>
                <h2 id={`${chapter.id}-title`}>{chapter.title}</h2>
                <div className="alf-narr-butler">
                  <img className="alf-bob" src={`/mascot/${chapter.pose}.png`} alt={chapter.poseAlt} />
                  <div className="alf-bubble">
                    <p className="alf-bubble-name">Butler</p>
                    <p>{chapter.say}</p>
                  </div>
                </div>
              </div>
              <div className="alf-stage">{chapter.screen}</div>
            </div>
          </section>
        ))}

        <section className="alf-cta" id="demo">
          <div className="alf-wrap alf-cta-in">
            {done ? <Confetti /> : null}
            <img className="alf-bob" src="/mascot/celebrating.png" alt="Butler celebrating" />
            <div>
              {home ? (
                <>
                  <h2>{done ? `You explored all ${total} stops!` : `Keep going: ${total - seen} ${total - seen === 1 ? "stop" : "stops"} left`}</h2>
                  <p>
                    {canDemo
                      ? "Now try it for real. Butler walks you through Algebra 1 as a teacher, then you see a lesson the way a student does. Nothing you do in the demo is saved."
                      : "That's the tour. Open any page from the sidebar to get started."}
                  </p>
                  {canDemo ? (
                    <div className="alf-actions">
                      <DemoButton className="alf-btn alf-btn--light" canDemo pending={false} />
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <h2>{canDemo ? "Now try it yourself" : "See it with your own courses"}</h2>
                  <p>
                    {canDemo
                      ? "Butler walks you through a real Algebra 1 course as a teacher, then you see a lesson the way a student does. Nothing you do in the demo is saved."
                      : "We'll walk you through Algebra 1 as a teacher, then as a student, and show you how your own courses would look here."}
                  </p>
                  <div className="alf-actions">
                    <DemoButton className="alf-btn alf-btn--light" canDemo={canDemo} pending={loading} />
                    {loading || signedIn ? null : (
                      <Link to="/auth" className="alf-btn alf-btn--outline">Sign in</Link>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      {home ? null : (
        <footer className="alf-wrap alf-footer">
          <span>AcuityLearn</span>
          <a href={INQUIRE_URL} target="_blank" rel="noopener noreferrer">
            Inquire here
          </a>
        </footer>
      )}
    </div>
  );
}

const CONFETTI_COLORS = ["#F59E0B", "#1BA3C6", "#F43F5E", "#10B981", "#FFFFFF", "#A78BFA"];

function Confetti() {
  return (
    <div className="alf-confetti" aria-hidden="true">
      {Array.from({ length: 14 }, (_, i) => (
        <span
          key={i}
          style={{
            left: `${i * 7 + 2}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${(i % 7) * 0.3}s`,
          }}
        />
      ))}
    </div>
  );
}
